import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../pages/AdminDashboard.css";
import { API_BASE_URL } from "../config/api";
import { resolveImageUrl } from "../utils/image";

const HEX_COLOR_REGEX = /^#([0-9A-F]{6})$/;
const PRODUCTS_CACHE_KEY = "admin_dashboard_products_cache";

const readCachedProducts = () => {
    if (typeof window === "undefined") return [];

    try {
        const cached = window.localStorage.getItem(PRODUCTS_CACHE_KEY);
        if (!cached) return [];

        const parsed = JSON.parse(cached);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.log("Unable to read products cache", error);
        return [];
    }
};

const writeCachedProducts = (items = []) => {
    if (typeof window === "undefined") return;

    try {
        window.localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(items));
    } catch (error) {
        console.log("Unable to write products cache", error);
    }
};

const normalizeHexCode = (value = "") => {
    const trimmed = value.trim();
    if (!trimmed) return "";

    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    const shortHexMatch = withHash.match(/^#([0-9A-Fa-f]{3})$/);

    if (shortHexMatch) {
        const [r, g, b] = shortHexMatch[1].split("");
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }

    return withHash.toUpperCase();
};

const parseColorLine = (line = "") => {
    const trimmed = line.trim();
    if (!trimmed) return null;

    const separators = [",", "\t", "|", ";"];
    for (const separator of separators) {
        if (trimmed.includes(separator)) {
            const parts = trimmed
                .split(separator)
                .map((part) => part.trim())
                .filter(Boolean);

            if (parts.length >= 2) {
                return { name: parts[0], hex: parts[parts.length - 1] };
            }
        }
    }

    let match = trimmed.match(
        /^(.*?)\s*[-:]\s*(#?(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))$/
    );
    if (match) {
        return { name: match[1], hex: match[2] };
    }

    match = trimmed.match(/^(.*?)\s+(#?(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))$/);
    if (match) {
        return { name: match[1], hex: match[2] };
    }

    return null;
};

export default function AdminDashboard() {
    const [activeTab, setActiveTab] = useState("view");
    const [product, setProduct] = useState({
        name: "",
        description: "",
        category: "",
        type: "simple",
        sku: "",
        image: null,
    });

    const [sizes, setSizes] = useState({
        quarter: { price: "", salePrice: "", sku: "" },
        gallon: { price: "", salePrice: "", sku: "" },
        drum: { price: "", salePrice: "", sku: "" },
    });

    const [colors, setColors] = useState([{ name: "", hex: "" }]);
    const [bulkColorInput, setBulkColorInput] = useState("");
    const [products, setProducts] = useState(() => readCachedProducts());
    const [editingId, setEditingId] = useState(null);
    const [bulkFile, setBulkFile] = useState(null);
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [colorsModalProduct, setColorsModalProduct] = useState(null);
    const navigate = useNavigate();
    const closeColorsModal = () => setColorsModalProduct(null);

    const fetchProducts = async () => {
        setIsProductsLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/api/products`, {
                params: { _ts: Date.now() },
            });
            const nextProducts = Array.isArray(res.data) ? res.data : [];
            setProducts(nextProducts);
            writeCachedProducts(nextProducts);
        } catch (err) {
            console.log(err);
            alert("Error fetching products");
        } finally {
            setIsProductsLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!colorsModalProduct) return undefined;

        const handleEscKey = (event) => {
            if (event.key === "Escape") setColorsModalProduct(null);
        };

        window.addEventListener("keydown", handleEscKey);
        return () => window.removeEventListener("keydown", handleEscKey);
    }, [colorsModalProduct]);

    const addBulkColors = () => {
        const lines = bulkColorInput
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);

        if (!lines.length) {
            alert("Paste at least one color line first.");
            return;
        }

        const validColors = [];
        const invalidLines = [];

        lines.forEach((line, index) => {
            const parsed = parseColorLine(line);

            if (!parsed) {
                invalidLines.push(index + 1);
                return;
            }

            const name = parsed.name.trim();
            const normalizedHex = normalizeHexCode(parsed.hex);

            if (!name || !HEX_COLOR_REGEX.test(normalizedHex)) {
                invalidLines.push(index + 1);
                return;
            }

            validColors.push({ name, hex: normalizedHex });
        });

        if (!validColors.length) {
            alert("No valid colors found. Use format: White, #FFFFFF");
            return;
        }

        const existingColors = colors
            .filter((c) => c.name?.trim() || c.hex?.trim())
            .map((c) => ({
                name: (c.name || "").trim(),
                hex: (c.hex || "").trim().toUpperCase(),
            }));

        const mergedColors = [...existingColors];
        const colorKeys = new Set(
            existingColors
                .map((c) => {
                    const normalizedHex = normalizeHexCode(c.hex);
                    if (!c.name || !HEX_COLOR_REGEX.test(normalizedHex)) return null;
                    return `${c.name.toLowerCase()}::${normalizedHex.toLowerCase()}`;
                })
                .filter(Boolean)
        );

        let addedCount = 0;
        let duplicateCount = 0;

        validColors.forEach((color) => {
            const key = `${color.name.toLowerCase()}::${color.hex.toLowerCase()}`;
            if (colorKeys.has(key)) {
                duplicateCount += 1;
                return;
            }

            colorKeys.add(key);
            mergedColors.push(color);
            addedCount += 1;
        });

        setColors(mergedColors.length ? mergedColors : [{ name: "", hex: "" }]);
        setBulkColorInput("");

        let message = `Added ${addedCount} colors.`;
        if (duplicateCount > 0) {
            message += ` ${duplicateCount} duplicate lines skipped.`;
        }
        if (invalidLines.length > 0) {
            message += ` Invalid line numbers: ${invalidLines.join(", ")}.`;
        }

        alert(message);
    };

    const [filterCategory, setFilterCategory] = useState("all");
    const submitProduct = async (e) => {
        e.preventDefault();
        const formData = new FormData();

        if (product.name) {
            const slug = product.name.toLowerCase().replace(/\s+/g, "-");
            formData.append("slug", slug);
        }

        for (let key in product) {
            if (product[key]) formData.append(key, product[key]);
        }

        formData.append(
            "sizes",
            JSON.stringify([
                {
                    size: "quarter",
                    price: sizes.quarter.price,
                    salePrice: sizes.quarter.salePrice,
                    sku: sizes.quarter.sku,
                },
                {
                    size: "gallon",
                    price: sizes.gallon.price,
                    salePrice: sizes.gallon.salePrice,
                    sku: sizes.gallon.sku,
                },
                {
                    size: "drum",
                    price: sizes.drum.price,
                    salePrice: sizes.drum.salePrice,
                    sku: sizes.drum.sku,
                },
            ])
        );

        formData.append("colors", JSON.stringify(colors.filter((c) => c.hex)));

        try {
            const token = localStorage.getItem("admin_token");
            if (editingId) {
                const res = await axios.put(
                    `${API_BASE_URL}/api/products/update/${editingId}`,
                    formData,
                    { headers: { Authorization: token, "Content-Type": "multipart/form-data" } }
                );
                if (res.data.success) {
                    alert("Product Updated!");
                    setEditingId(null);
                }
            } else {
                const res = await axios.post(
                    `${API_BASE_URL}/api/products/add`,
                    formData,
                    { headers: { Authorization: token, "Content-Type": "multipart/form-data" } }
                );
                if (res.data.success) {
                    alert("Product Added!");
                }
            }

            setProduct({
                name: "",
                description: "",
                category: "",
                type: "simple",
                sku: "",
                image: null,
            });
            setSizes({
                quarter: { price: "", salePrice: "", sku: "" },
                gallon: { price: "", salePrice: "", sku: "" },
                drum: { price: "", salePrice: "", sku: "" },
            });
            setColors([{ name: "", hex: "" }]);
            setBulkColorInput("");

            fetchProducts();
            setActiveTab("view");
        } catch (err) {
            console.log(err);
            const serverMessage =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Error submitting product";
            alert(serverMessage);
        }
    };

    const deleteProduct = async (id) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            const token = localStorage.getItem("admin_token");
            await axios.delete(`${API_BASE_URL}/api/products/delete/${id}`, {
                headers: { Authorization: token },
            });
            alert("Product deleted!");
            setProducts(products.filter((p) => p._id !== id));
        } catch (err) {
            console.log(err);
            alert("Error deleting product");
        }
    };

    const editProduct = (p) => {
        setEditingId(p._id);
        setProduct({
            name: p.name,
            description: p.description,
            category: p.category,
            type: p.type,
            sku: p.sku,
            image: null,
        });

        const loadedSizes = { quarter: {}, gallon: {}, drum: {} };
        p.sizes.forEach((s) => {
            loadedSizes[s.size] = { price: s.price, salePrice: s.salePrice, sku: s.sku };
        });
        setSizes(loadedSizes);

        setColors(p.colors && p.colors.length > 0 ? p.colors : [{ name: "", hex: "" }]);
        setBulkColorInput("");
        setActiveTab("add");
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        navigate("/");
    };

    // ✅ Bulk upload handler
    const uploadBulkProducts = async () => {
        if (!bulkFile) {
            alert("Please select a CSV file first");
            return;
        }

        const formData = new FormData();
        formData.append("file", bulkFile);

        try {
            const token = localStorage.getItem("admin_token");
            const res = await axios.post(
                `${API_BASE_URL}/api/products/bulk-upload`,
                formData,
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            alert(`Uploaded ${res.data.inserted} products successfully`);
            setBulkFile(null);
            fetchProducts();
            setActiveTab("view");
        } catch (err) {
            console.log(err);
            alert("Bulk upload failed");
        }
    };

    const filteredProducts =
        filterCategory === "all"
            ? products
            : products.filter((p) => p.category === filterCategory);

    const categoryStats = useMemo(() => {
        const counts = products.reduce((accumulator, productItem) => {
            const rawCategory =
                typeof productItem.category === "string" ? productItem.category.trim() : "";
            const normalizedCategory = rawCategory || "Uncategorized";

            accumulator[normalizedCategory] = (accumulator[normalizedCategory] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(counts)
            .sort((left, right) => {
                if (right[1] !== left[1]) return right[1] - left[1];
                return left[0].localeCompare(right[0]);
            })
            .map(([category, count], index) => ({
                rank: index + 1,
                category,
                count,
            }));
    }, [products]);

    const emptyProductsMessage =
        products.length === 0
            ? "No products found."
            : "No products found for selected category.";

    return (
        <div className="admin-dashboard">
            <div className="sidebar">
                <h2>Admin Panel</h2>
                <ul>
                    <li
                        className={activeTab === "view" ? "active" : ""}
                        onClick={() => setActiveTab("view")}
                    >
                        View Products
                    </li>

                    <li
                        className={activeTab === "add" ? "active" : ""}
                        onClick={() => {
                            setActiveTab("add");
                            setEditingId(null);
                        }}
                    >
                        Add Product
                    </li>


                    {/* ✅ NEW TAB */}
                    <li
                        className={activeTab === "bulk" ? "active" : ""}
                        onClick={() => setActiveTab("bulk")}
                    >
                        Bulk Upload
                    </li>

                    <li
                        onClick={handleLogout}
                        style={{ marginTop: "auto", color: "#e74c3c", cursor: "pointer" }}
                    >
                        Logout
                    </li>
                </ul>
            </div>

            <div className="dashboard-content">
                {activeTab === "add" && (
                    <div>
                        <h2>{editingId ? "Edit Product" : "Add New Product"}</h2>
                        <form onSubmit={submitProduct} className="dashboard-form">
                            <div className="form-group">
                                <label>Product Name</label>
                                <input
                                    type="text"
                                    value={product.name}
                                    onChange={(e) => setProduct({ ...product, name: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={product.description}
                                    onChange={(e) =>
                                        setProduct({ ...product, description: e.target.value })
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={product.category}
                                    onChange={(e) =>
                                        setProduct({ ...product, category: e.target.value })
                                    }
                                >
                                    <option value="">Select Category</option>
                                    <option value="Glamur Paints">Glamur Paints</option>
                                    <option value="Dulux Paints">Dulux Paints</option>
                                    <option value="Corona Paints">Corona Paints</option>
                                    <option value="Diamond Paints">Diamond Paints</option>
                                    <option value="Brighto Paints">Brighto Paints</option>
                                    <option value="Nelson Paints">Nelson Paints</option>
                                    <option value="Gobi's Paints">Gobi's Paints</option>
                                    <option value="Berger Paints">Berger Paints</option>
                                    <option value="Kansai Paints">Kansai Paints</option>
                                    <option value="Reliance Paints">Reliance Paints</option>
                                    <option value="Reliable Paints">Reliable Paints</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Product Type</label>
                                <select
                                    value={product.type}
                                    onChange={(e) => setProduct({ ...product, type: e.target.value })}
                                >
                                    <option value="simple">Simple</option>
                                    <option value="variable">Variable</option>
                                </select>
                            </div>

                            <h4>Product Sizes | Prices | SKU</h4>
                            {["quarter", "gallon", "drum"].map((size) => (
                                <div className="form-group" key={size}>
                                    <label>{size.charAt(0).toUpperCase() + size.slice(1)}</label>

                                    <input
                                        type="number"
                                        placeholder="Regular Price"
                                        value={sizes[size].price}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], price: e.target.value },
                                            })
                                        }
                                    />

                                    <input
                                        type="number"
                                        placeholder="Sale Price"
                                        value={sizes[size].salePrice}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], salePrice: e.target.value },
                                            })
                                        }
                                    />

                                    <input
                                        type="text"
                                        placeholder="SKU"
                                        value={sizes[size].sku}
                                        onChange={(e) =>
                                            setSizes({
                                                ...sizes,
                                                [size]: { ...sizes[size], sku: e.target.value },
                                            })
                                        }
                                    />
                                </div>
                            ))}

                            <h3>Color Shades (HEX)</h3>
                            <div className="form-group bulk-color-box">
                                <label>Quick Add Multiple Colors</label>
                                <textarea
                                    rows={5}
                                    value={bulkColorInput}
                                    onChange={(e) => setBulkColorInput(e.target.value)}
                                    placeholder={`White, #FFFFFF\nIvory, FFF8E7\nLight Gray - #D1D5DB`}
                                />
                                <small className="bulk-color-hint">
                                    One color per line. Supported: comma, tab, pipe, semicolon, dash, or
                                    colon.
                                </small>
                                <button
                                    type="button"
                                    className="edit-btn bulk-color-btn"
                                    onClick={addBulkColors}
                                >
                                    Add Pasted Colors
                                </button>
                            </div>

                            {colors.map((c, idx) => (
                                <div key={idx} className="form-group color-input">
                                    {/* Color Preview Card */}
                                    <div
                                        className="color-preview"
                                        style={{
                                            backgroundColor: /^#([0-9A-Fa-f]{6})$/.test(c.hex)
                                                ? c.hex
                                                : "#e5e7eb",
                                        }}
                                        title={c.hex || "No color"}
                                    />

                                    <input
                                        type="text"
                                        placeholder="Color Name"
                                        value={c.name}
                                        onChange={(e) => {
                                            const newColors = [...colors];
                                            newColors[idx].name = e.target.value;
                                            setColors(newColors);
                                        }}
                                    />

                                    <input
                                        type="text"
                                        placeholder="#FFFFFF"
                                        value={c.hex}
                                        onChange={(e) => {
                                            const newColors = [...colors];
                                            newColors[idx].hex = e.target.value.toUpperCase();
                                            setColors(newColors);
                                        }}
                                        pattern="^#([0-9A-Fa-f]{6})$"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newColors = colors.filter((_, i) => i !== idx);
                                            setColors(newColors.length ? newColors : [{ name: "", hex: "" }]);
                                        }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => setColors([...colors, { name: "", hex: "" }])}
                            >
                                Add Color
                            </button>

                            <div className="form-group">
                                <label>Product Image</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        setProduct({ ...product, image: e.target.files[0] })
                                    }
                                />
                            </div>

                            <button type="submit" className="submit-btn">
                                {editingId ? "Update Product" : "Add Product"}
                            </button>
                        </form>
                    </div>
                )}

                {activeTab === "bulk" && (
                    <div>
                        <h2>Bulk Product Upload (CSV)</h2>
                        <p>Upload a CSV with columns: name, description, category, type, sizes, colors</p>

                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setBulkFile(e.target.files[0])}
                        />

                        <button
                            className="submit-btn"
                            style={{ marginTop: 10 }}
                            type="button"
                            onClick={uploadBulkProducts}
                        >
                            Upload CSV
                        </button>

                        <div style={{ marginTop: 12, fontSize: 13 }}>
                            {/* <b>CSV Example:</b>
                            <pre style={{ whiteSpace: "pre-wrap" }}>
                                {`name,description,category,type,sizes,colors
Royal Emulsion,Interior paint,Glamur Paints,simple,"[{""size"":""quarter"",""price"":1200,""salePrice"":1100,""sku"":""RE-Q""}]","[{""name"":""White"",""hex"":""#FFFFFF""}]"`
                                }
                            </pre> */}
                        </div>
                    </div>
                )}

                {activeTab === "view" && (
                    <div>
                        <h2>View Products</h2>

                        <div className="dashboard-overview">
                            <article className="overview-card">
                                <p>Total Products</p>
                                <strong>{products.length}</strong>
                                <span>All catalog items</span>
                            </article>

                            <article className="overview-card">
                                <p>Categories</p>
                                <strong>{categoryStats.length}</strong>
                                <span>Categories with products</span>
                            </article>

                            <article className="overview-card">
                                <p>Filtered Results</p>
                                <strong>{filteredProducts.length}</strong>
                                <span>
                                    {filterCategory === "all"
                                        ? "Showing all categories"
                                        : filterCategory}
                                </span>
                            </article>
                        </div>

                        <section className="category-insights">
                            <div className="category-insights-head">
                                <h3>Products by Category</h3>
                                <span>{categoryStats.length} listed</span>
                            </div>

                            {categoryStats.length > 0 ? (
                                <div className="category-insights-grid">
                                    {categoryStats.map((categoryItem) => (
                                        <article
                                            key={categoryItem.category}
                                            className="category-insight-card"
                                        >
                                            <span className="category-rank">
                                                {String(categoryItem.rank).padStart(2, "0")}
                                            </span>
                                            <div className="category-meta">
                                                <h4>{categoryItem.category}</h4>
                                                <p>
                                                    {categoryItem.count}{" "}
                                                    {categoryItem.count === 1
                                                        ? "product"
                                                        : "products"}
                                                </p>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            ) : (
                                !isProductsLoading && (
                                    <p className="view-status">No category data available yet.</p>
                                )
                            )}
                        </section>

                        <div className="filter-container">
                            <label>Filter by Category:</label>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Categories</option>
                                <option value="Glamur Paints">Glamur Paints</option>
                                <option value="Dulux Paints">Dulux Paints</option>
                                <option value="Corona Paints">Corona Paints</option>
                                <option value="Diamond Paints">Diamond Paints</option>
                                <option value="Brighto Paints">Brighto Paints</option>
                                <option value="Nelson Paints">Nelson Paints</option>
                                <option value="Gobi's Paints">Gobi's Paints</option>
                                <option value="Berger Paints">Berger Paints</option>
                                <option value="Kansai Paints">Kansai Paints</option>
                                <option value="Reliance Paints">Reliance Paints</option>
                                <option value="Reliable Paints">Reliable Paints</option>
                            </select>
                        </div>

                        <div className="product-table-wrap">
                            <table className="product-table">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Image</th>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>SKU</th>
                                        <th className="colors-col">Colors</th>
                                        <th>Quarter</th>
                                        <th>Gallon</th>
                                        <th>Drum</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredProducts.map((p, index) => {
                                        const sizesData = { quarter: {}, gallon: {}, drum: {} };

                                        if (Array.isArray(p.sizes)) {
                                            p.sizes.forEach((s) => {
                                                sizesData[s.size] = s;
                                            });
                                        }

                                        return (
                                            <tr key={p._id}>
                                                <td data-label="No">{index + 1}</td>

                                                <td data-label="Image" className="product-image-cell">
                                                    {p.image ? (
                                                        <img
                                                            src={resolveImageUrl(p.image)}
                                                            alt={p.name}
                                                            className="product-thumb"
                                                            loading="lazy"
                                                            decoding="async"
                                                        />
                                                    ) : (
                                                        <span className="product-image-empty">No Image</span>
                                                    )}
                                                </td>

                                                <td data-label="Name">{p.name}</td>
                                                <td data-label="Category">{p.category}</td>

                                                <td data-label="SKU" className="sku-col">
                                                    {p.sizes?.length ? (
                                                        <div className="sku-lines">
                                                            {p.sizes.map((s, skuIndex) => (
                                                                <span key={`${p._id}-sku-${s.size}-${skuIndex}`}>
                                                                    {s.size}: {s.sku || "N/A"}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>

                                                <td data-label="Colors" className="colors-col">
                                                    {p.colors?.length ? (
                                                        <div className="colors-cell-content">
                                                            <p className="colors-preview-text">
                                                                {p.colors
                                                                    .slice(0, 2)
                                                                    .map(
                                                                        (c) =>
                                                                            `${c.name || "Unnamed"} (${c.hex})`
                                                                    )
                                                                    .join(" | ")}
                                                                {p.colors.length > 2 &&
                                                                    ` +${p.colors.length - 2} more`}
                                                            </p>
                                                            <button
                                                                type="button"
                                                                className="view-colors-btn"
                                                                onClick={() =>
                                                                    setColorsModalProduct({
                                                                        name: p.name,
                                                                        colors: p.colors,
                                                                    })
                                                                }
                                                            >
                                                                View Colors ({p.colors.length})
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>

                                                <td data-label="Quarter" className="size-price-col">
                                                    {sizesData.quarter.price ? (
                                                        <div className="size-price-lines">
                                                            <span>Rs.{sizesData.quarter.price}</span>
                                                            <span>Sale: Rs.{sizesData.quarter.salePrice || 0}</span>
                                                        </div>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </td>

                                                <td data-label="Gallon" className="size-price-col">
                                                    {sizesData.gallon.price ? (
                                                        <div className="size-price-lines">
                                                            <span>Rs.{sizesData.gallon.price}</span>
                                                            <span>Sale: Rs.{sizesData.gallon.salePrice || 0}</span>
                                                        </div>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </td>

                                                <td data-label="Drum" className="size-price-col">
                                                    {sizesData.drum.price ? (
                                                        <div className="size-price-lines">
                                                            <span>Rs.{sizesData.drum.price}</span>
                                                            <span>Sale: Rs.{sizesData.drum.salePrice || 0}</span>
                                                        </div>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </td>

                                                <td data-label="Actions" className="actions-col">
                                                    <div className="actions-buttons">
                                                        <button
                                                            className="edit-btn"
                                                            onClick={() => editProduct(p)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => deleteProduct(p._id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {!isProductsLoading && filteredProducts.length === 0 && (
                            <p className="view-status">{emptyProductsMessage}</p>
                        )}

                        {colorsModalProduct && (
                            <div
                                className="colors-modal-backdrop"
                                onClick={closeColorsModal}
                                role="presentation"
                            >
                                <div
                                    className="colors-modal"
                                    role="dialog"
                                    aria-modal="true"
                                    aria-labelledby="colors-modal-title"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <div className="colors-modal-header">
                                        <h3 id="colors-modal-title">
                                            {colorsModalProduct.name} Colors
                                        </h3>
                                        <button
                                            type="button"
                                            className="modal-close-btn"
                                            onClick={closeColorsModal}
                                        >
                                            Close
                                        </button>
                                    </div>
                                    <div className="colors-modal-list">
                                        {colorsModalProduct.colors.map((color, index) => {
                                            const normalizedHex = normalizeHexCode(color?.hex || "");
                                            const hasValidHex = HEX_COLOR_REGEX.test(normalizedHex);
                                            return (
                                                <div
                                                    className="colors-modal-item"
                                                    key={`${color?.name || "color"}-${color?.hex || "-"}-${index}`}
                                                >
                                                    <span
                                                        className="colors-swatch"
                                                        style={{
                                                            backgroundColor: hasValidHex
                                                                ? normalizedHex
                                                                : "#e5e7eb",
                                                        }}
                                                    />
                                                    <span className="colors-modal-name">
                                                        {color?.name || "Unnamed color"}
                                                    </span>
                                                    <span className="colors-modal-hex">
                                                        {hasValidHex ? normalizedHex : color?.hex || "-"}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
